import { Handshake, UserPlus } from "lucide-react";
import { defineFeature } from "../types";

/**
 * CLIENT PORTAL (PRD §8 Item 9).
 *
 * Two audiences, one feature folder:
 *
 *  · `portalRoutes` are the CLIENT's screens, mounted inside the agency-branded
 *    `PortalShell` at /portal. They talk only to `/api/portal/v1` with the
 *    `l2a_portal` cookie, and they are the only place in this app that renders
 *    anything for somebody who is not a member of the workspace.
 *
 *  · `routes` are the AGENCY's screens at /client-portal: who holds a key, what
 *    is shared, what the client has opened, and the read-only preview. They are
 *    ordinary staff pages behind `portal:manage`.
 *
 * The two never share a fetcher, a cache key or a session, which is the whole
 * reason a contact who is also a staff user can hold both at once.
 */
export default defineFeature({
  key: "portal",
  title: "Client portal",

  portalRoutes: [
    { index: true, lazy: () => import("./page") },
    { path: "login", lazy: () => import("./login") },
    { path: "approvals", lazy: () => import("./approvals") },
    { path: "approvals/:id", lazy: () => import("./approval-detail") },
    { path: "projects", lazy: () => import("./projects") },
    { path: "projects/:id", lazy: () => import("./project-detail") },
    { path: "invoices", lazy: () => import("./invoices") },
    { path: "invoices/:id", lazy: () => import("./invoice-detail") },
    { path: "statement", lazy: () => import("./statement") },
    { path: "documents", lazy: () => import("./documents") },
    { path: "files", lazy: () => import("./files") },
    { path: "messages", lazy: () => import("./messages") },
    { path: "profile", lazy: () => import("./profile") },
    // `/portal/<workspace>` — the short address printed on invoices. Static
    // segments above always win over this one; the handle tells the shell it is
    // an entrance (send a signed-out visitor to sign-in WITH the workspace).
    { path: ":workspace", handle: { portalEntry: true }, lazy: () => import("./workspace") },
  ],

  // The approval e-mail's link: a proof decided in two taps with no portal sign-in.
  publicRoutes: [{ path: "/review/:token", lazy: () => import("./public-review") }],

  routes: [
    { path: "client-portal", lazy: () => import("./admin/page") },
    { path: "client-portal/:clientId", lazy: () => import("./admin/client") },
  ],

  nav: [
    {
      key: "client-portal",
      label: "Client portal",
      to: "/client-portal",
      icon: Handshake,
      group: "clients",
      order: 30,
      permission: "portal:manage",
      description: "Portal access, what clients can see, and what they've opened",
    },
  ],

  quickCreate: [
    {
      key: "portal-access",
      label: "Portal invitation",
      icon: UserPlus,
      to: "/client-portal?invite=1",
      permission: "portal:manage",
      order: 60,
    },
  ],

  commands: [
    {
      key: "portal.shared",
      label: "See what a client can view",
      icon: Handshake,
      to: "/client-portal",
      permission: "portal:manage",
      group: "actions",
      keywords: ["portal", "share", "visibility", "preview as client"],
    },
  ],
});
